import { FormikProps } from 'formik';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSavedViewStore } from '../..';
import useClosable from '../../hooks/useClosable';
import { SavedViewDraft } from '../../interfaces/ISavedView';
import { ClosableOrder } from '../../store/ClosableStore';
import commonStyles from '../Common.module.css';
import editStyles from '../Edit.module.css';
import EditControls from '../EditControls';
import HintIcon from '../icons/HintIcon';
import Modal from '../Modal';
import savedViewsStyles from "./SavedViews.module.css";
import SavedViewsCreate from './SavedViewsCreate';
import SavedViewsFields from './SavedViewsFields';
import SavedViewsUpdate from './SavedViewsUpdate';

function SavedViews() {
    const navigate = useNavigate();
    const savedViewStore = useSavedViewStore();

    const [mode, setMode] = useState(SavedViewMode.AddSavedView);
    const [activeFormik, setActiveFormik] = useState<ActiveFormik>(null);
    const primaryLabel = useMemo(() => (mode === SavedViewMode.AddSavedView ? "Create" : "Update"), [mode]);

    const handleSubmit = () => activeFormik?.submitForm();
    const close = () => navigate("/");
    useClosable(ClosableOrder.Medium, close);

    return (
        <Modal header="Save/Update View" midWidth onClose={close}>
            <div className={savedViewsStyles.columns}>
                <div className={savedViewsStyles.column}>
                    <SavedViewsCreate
                        savedViewStore={savedViewStore}
                        isActive={mode === SavedViewMode.AddSavedView}
                        onSelect={() => setMode(SavedViewMode.AddSavedView)}
                        onActiveFormik={setActiveFormik}
                        onDone={close}
                    />

                    <SavedViewsUpdate
                        savedViewStore={savedViewStore}
                        isActive={mode === SavedViewMode.UpdateSavedView}
                        onSelect={() => setMode(SavedViewMode.UpdateSavedView)}
                        onActiveFormik={setActiveFormik}
                        onDone={close}
                    />

                    <div className={savedViewsStyles.hintRow}>
                        <HintIcon />
                        <span className={savedViewsStyles.hintText}>
                            Saved Views can be recalled from your Favorites list.
                        </span>
                    </div>
                </div>

                <div className={savedViewsStyles.column}>
                    {activeFormik && <SavedViewsFields formik={activeFormik} />}
                </div>
            </div>

            <div className={editStyles.column}>
                <div className={commonStyles.controls}>
                    <div className={commonStyles.controlGroup}>
                        <EditControls
                            isStateChanged={false}
                            isUpdateLoading={false}
                            onUpdate={handleSubmit}
                            primaryLabel={primaryLabel}
                            onCancel={close}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
}

export default observer(SavedViews);

enum SavedViewMode {
    AddSavedView,
    UpdateSavedView,
}

type ActiveFormik = FormikProps<SavedViewDraft> | null;